import * as React from "react";
import { useRef, useCallback, ReactNode } from "react";
import { ApiVersionName, esd_storage } from "./Domain";

/**
 * @description 用於function的debounce，防止頻繁觸發指定function
 * @param callback 要debounce的 callback function
 * @param delay 要延遲多久，預設700ms
 * @warning 如果你是直接將event傳入,event會在處理非同步操作時被清空，這會導致event是空的
 * @example 
 *  const debounceUpdateState = useDebounce(async (name: string, value: string) => {
        setState((prev) => ({
            ...prev,
            [name]: value,
        }));
    })
    //用在元件上
    const debounceSearchHandleInput = (e: HandleEvent) => {
        const name = e.target.name.split("--")[1];
        const value = e.target.value;

        debounceUpdateState(name, value);
    };
 */

export const useDebounce = <T extends (...args: any[]) => void>(callback: T, delay: number = 700) => {
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    return useCallback((...args: Parameters<T>) => {
        if (timer.current) clearTimeout(timer.current); // 清除前一個 timer

        timer.current = setTimeout(() => {
            callback(...args); // 執行 function
        }, delay);
    }, [callback, delay]);
};

/**
 * @description 用於function的debounce，防止頻繁觸發指定function
 * @param callback 要debounce的 callback function
 * @param delay 要延遲多久，預設700ms
 * @example 
 * //宣告
 * const func = debounceFn(func)
 * 
 * //在component中
 * onChange={func}
 */
export function debounceFn<F extends (...args: any[]) => void>(
    func: F,
    wait = 700
): F {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return ((...args: any[]) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    }) as F;
}


/**
 * @description 用於判別和區分不同環境下的UI及邏輯
 * @return {boolean} isDotNet6Version 是否是DotNet6環境
 * @return {boolean} isFrameworkVersion 是否是Framework環境
 * @return {boolean} isDotNet6AMNVersion 是否是DotNet6AMN環境
 * @return {React.Node} DotNet6 只會在DotNet6環境下才render Childen
 * @example <DotNet6><Childen/></DotNet6> Childen View只會在DotNet6環境下才顯示
 * @return {React.Node} Framework 只會在Framework環境下才render Childen
 * @example <Framework><Childen/></Framework> Childen View只會在Framework環境下才顯示
 */
export const useApiVersion = () => {
    const defaultApiVersion = localStorage.getItem('defaultApiVersion') as ApiVersionName || ApiVersionName.DOT_NET_6;

    const isDotNet6Version = defaultApiVersion === ApiVersionName.DOT_NET_6
    const isFrameworkVersion = defaultApiVersion === ApiVersionName.FRAMEWORK;
    const isDotNet6AMNVersion = defaultApiVersion === ApiVersionName.DOT_NET_6_AM_N;

    const DotNet6 = ({ children }: { children: ReactNode }) => <>
        {isDotNet6Version && children}
    </>

    const Framework = ({ children }: { children: ReactNode }) => <>
        {isFrameworkVersion && children}
    </>

    const DotNet6AMN = ({ children }: { children: ReactNode }) => <>
        {isDotNet6AMNVersion && children}
    </>

    return {
        isDotNet6Version,
        isFrameworkVersion,
        isDotNet6AMNVersion,
        DotNet6,
        DotNet6AMN,
        Framework,
    }
}